import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {Trophy, User, LogOut, Award} from 'lucide-react'
import { lumi } from '@/lib/lumi'
import { useAuth } from '@/hooks/useAuth'


// Get optimized canvas dimensions for desktop/mobile
const getCanvasDimensions = () => {
  const width = window.innerWidth
  const height = window.innerHeight
  const aspectRatio = width / height
  
  // Desktop optimization: cap width for better proportions
  if (width > 1200 && aspectRatio > 1.5) {
    const optimalWidth = Math.min(width, height * 1.6)
    return { width: optimalWidth, height }
  }
  
  return { width, height }
}

interface Candle {
  x: number
  y: number
  width: number
  height: number
  scored?: boolean
  isTop?: boolean
}

interface Moon {
  x: number
  y: number
  size: number
  collected: boolean
}

function Game() {
  const navigate = useNavigate()
  const { user, signOut } = useAuth()
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [gameStarted, setGameStarted] = useState(false)
  const [gameOver, setGameOver] = useState(false)
  const [score, setScore] = useState(0)
  const [highScore, setHighScore] = useState(0)
  const [difficulty, setDifficulty] = useState(1)
  const [scoreSubmitted, setScoreSubmitted] = useState(false)
  const [canvasSize, setCanvasSize] = useState(getCanvasDimensions())
  const [burnMeterFull, setBurnMeterFull] = useState(false)
  const [burnMeterData, setBurnMeterData] = useState<any>(null)

  const bunnyImageRef = useRef<HTMLImageElement | null>(null)
  const animationRef = useRef<number | null>(null)
  const gameLoopRunning = useRef(false)

  // Responsive dimensions optimized for desktop
  const CANVAS_WIDTH = canvasSize.width
  const CANVAS_HEIGHT = canvasSize.height
  const isMobile = CANVAS_WIDTH < 768
  const isDesktop = CANVAS_WIDTH >= 1200
  
  // Desktop-optimized bunny size (smaller on large screens)
  const BUNNY_SIZE = isMobile 
    ? Math.max(40, CANVAS_WIDTH * 0.075)
    : isDesktop 
    ? Math.min(60, CANVAS_WIDTH * 0.04)
    : Math.max(50, CANVAS_WIDTH * 0.055)
  
  const BUNNY_X = CANVAS_WIDTH * 0.15
  
  // Desktop-optimized physics
  const GRAVITY = isDesktop ? 0.6 : 0.5
  const JUMP_FORCE = isDesktop ? -11 : -10
  const BASE_SPEED = isDesktop ? 4 : 3

  // Game state
  const gameState = useRef({
    bunnyY: CANVAS_HEIGHT / 2 - BUNNY_SIZE / 2,
    bunnyVelocity: 0,
    candles: [] as Candle[],
    moons: [] as Moon[],
    frameCount: 0,
    starOffset: 0,
    cloudOffset: 0,
    localScore: 0,
    localDifficulty: 1,
    graceFrames: 0
  })
  
  // Moon collection sound effect
  const moonSoundRef = useRef<HTMLAudioElement | null>(null)
  
  useEffect(() => {
    // Using a coin/pickup sound from freesound (similar to Sonic ring)
    moonSoundRef.current = new Audio('https://cdn.freesound.org/previews/320/320655_5260872-lq.mp3')
    moonSoundRef.current.volume = 0.5
    moonSoundRef.current.load()
  }, [])

  // Check burn meter status on mount
  useEffect(() => {
    checkBurnMeter()
  }, [])

  const checkBurnMeter = async () => {
    try {
      const result = await lumi.entities.burn_meter.list({ limit: 1 })
      console.log('🔍 Burn meter check result:', result)
      if (result?.list?.[0]) {
        const meter = result.list[0]
        console.log('✅ Burn meter data:', meter)
        setBurnMeterData(meter)
        setBurnMeterFull(!meter.isActive || meter.currentAmount >= meter.maxAmount)
      } else {
        console.warn('⚠️ No burn meter found in database')
      }
    } catch (error) {
      console.error('❌ Failed to check burn meter:', error)
    }
  }

  // Preload bunny image and render initial background
  useEffect(() => {
    const img = new Image()
    img.src = "https://static.lumi.new/7b/7b3b9738bd14cb21ec880e638b7612fd.webp"
    img.onload = () => {
      bunnyImageRef.current = img
      console.log('✅ Bunny loaded!')
    }
    img.onerror = () => console.error('❌ Bunny load failed')
    
    // Render initial background immediately
    setTimeout(() => renderBackground(), 100)
  }, [])

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const newSize = getCanvasDimensions()
      setCanvasSize(newSize)
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Submit score to database
  const submitScore = async (finalScore: number, forceSubmit: boolean = false) => {
    if (!user || finalScore === 0) {
      console.log('⏭️ Skipping score submission:', !user ? 'no user' : 'zero score')
      return
    }
    
    // Validate user object has required fields
    const userId = user.userId || user.user?.userId
    if (!userId) {
      console.error('❌ Cannot submit score: user.userId is missing', { message: '❌ Cannot submit score: user.userId is missing', pathname: window.location.pathname })
      console.error('Full user object:', JSON.stringify(user, null, 2))
      return
    }
    
    try {
      const now = new Date().toISOString()
      console.log('📤 Submitting score:', finalScore, 'for user:', user.userId, 'scoreSubmitted:', scoreSubmitted, 'forceSubmit:', forceSubmit)
      // Submit if forced OR not already submitted for this game
      if (forceSubmit || !scoreSubmitted) {
        const scoreData = {
          userId: userId,
          username: user.userName || user.user?.userName || user.email?.split('@')[0] || user.user?.email?.split('@')[0] || 'Player',
          score: finalScore,
          difficulty: gameState.current.localDifficulty,
          creator: userId,
          creatorEmail: user.email || user.user?.email || '',
          createdAt: now,
          updatedAt: now
        }
        console.log('📋 Score data to submit:', JSON.stringify(scoreData, null, 2))
        
        await lumi.entities.scores.create(scoreData)
        console.log('✅ Score submitted successfully:', finalScore)
        return true
      } else {
        console.log('⏭️ Score already submitted for this game session')
        return false
      }
    } catch (error) {
      console.error('❌ Failed to submit score:', JSON.stringify(error, null, 2))
      console.error('Error details:', error)
      return false
    }
  }

  // Update burn meter after game
  const updateBurnMeter = async (moonsCollected: number) => {
    console.log('🎯 updateBurnMeter called with:', moonsCollected, 'moons')
    console.log('📊 Current burnMeterData:', JSON.stringify(burnMeterData, null, 2))
    
    if (moonsCollected === 0) {
      console.log('⏭️ Skipping burn meter update (no moons collected)')
      return
    }
    
    try {
      // Always fetch fresh meter data
      const result = await lumi.entities.burn_meter.list({ limit: 1 })
      console.log('📥 Fetched burn meter:', JSON.stringify(result, null, 2))
      
      if (!result?.list?.[0]) {
        console.error('❌ No burn meter found in database')
        return
      }
      
      const meter = result.list[0]
      console.log('🔍 Meter ID type:', typeof meter._id, 'Value:', meter._id)
      console.log('🔍 Current amount type:', typeof meter.currentAmount, 'Value:', meter.currentAmount)
      console.log('🔍 Max amount type:', typeof meter.maxAmount, 'Value:', meter.maxAmount)
      
      // Validate ID is string
      if (typeof meter._id !== 'string' || !meter._id) {
        console.error('❌ Invalid meter ID:', meter._id)
        return
      }
      
      // Calculate new values with proper types
      const currentAmount = Number(meter.currentAmount) || 0
      const maxAmount = Number(meter.maxAmount) || 25000
      const newAmount = currentAmount + moonsCollected
      const isNowFull = newAmount >= maxAmount
      
      console.log(`🔄 Updating burn meter: ${currentAmount} → ${newAmount} (max: ${maxAmount}) [+${moonsCollected} moons]`)
      
      // Prepare update data with proper types
      const updateData = {
        currentAmount: newAmount,
        isActive: !isNowFull,
        updatedAt: new Date().toISOString()
      }
      
      console.log('📤 Update payload:', JSON.stringify(updateData, null, 2))
      console.log('📤 Calling update with ID:', meter._id)
      
      // Call update with correct parameter order: (id, data)
      const updateResult = await lumi.entities.burn_meter.update(meter._id, updateData)
      
      console.log(`✅ Burn meter updated successfully:`, JSON.stringify(updateResult, null, 2))
      console.log(`✅ Added ${moonsCollected} tokens (${newAmount}/${maxAmount})`)
      
      // Refresh meter data
      await checkBurnMeter()
    } catch (error: any) {
      console.error('❌ Failed to update burn meter:')
      console.error('Error type:', typeof error)
      console.error('Error object:', JSON.stringify(error, null, 2))
      console.error('Error message:', error?.message || 'No message')
      console.error('Error stack:', error?.stack || 'No stack')
      if (error?.response) {
        console.error('API Response:', JSON.stringify(error.response, null, 2))
      }
    }
  }

  // End game function
  const endGame = async () => {
    if (gameLoopRunning.current === false) return
    
    gameLoopRunning.current = false
    
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current)
      animationRef.current = null
    }

    const finalScore = gameState.current.localScore
    console.log('🎮 Game ending with score:', finalScore, 'scoreSubmitted:', scoreSubmitted)
    setScore(finalScore)
    
    if (finalScore > highScore) {
      setHighScore(finalScore)
    }
    
    setGameOver(true)
    setGameStarted(false)
    
    // ATOMIC TRANSACTION: Submit score and update burn meter together
    if (user && finalScore > 0) {
      console.log('💾 [ATOMIC] Starting score submission and burn meter update for', finalScore, 'moons')
      console.log('💾 [ATOMIC] User:', user.userId, user.userName || user.email)
      console.log('🔄 [ATOMIC] Resetting scoreSubmitted flag:', scoreSubmitted, '→ false')
      
      // Reset flag BEFORE attempting submission
      setScoreSubmitted(false)
      
      try {
        // Step 1: Submit score to database (force submit to guarantee execution)
        console.log('📝 [STEP 1/2] Submitting score to database...')
        const scoreSubmitSuccess = await submitScore(finalScore, true)
        
        if (scoreSubmitSuccess) {
          console.log('✅ [STEP 1/2] Score submitted successfully')
          // Mark as submitted only after successful submission
          setScoreSubmitted(true)
        } else {
          console.warn('⚠️ [STEP 1/2] Score submission returned false')
        }
        
        // Step 2: Update burn meter (execute regardless of score submission result)
        console.log('🔥 [STEP 2/2] Updating burn meter...')
        await updateBurnMeter(finalScore)
        console.log('✅ [STEP 2/2] Burn meter updated successfully')
        
        console.log('🎉 [ATOMIC] All updates completed! Score:', scoreSubmitSuccess ? 'SAVED' : 'FAILED', '| Burn meter: UPDATED')
      } catch (error) {
        console.error('❌ [ATOMIC] Failed to complete updates:', error)
      }
    } else {
      console.log('⏭️ Score submission skipped:', { user: !!user, finalScore })
      if (!user) console.log('👤 Not logged in - skipping all database updates')
      if (finalScore === 0) console.log('0️⃣ No moons collected - skipping all database updates')
    }
  }

  // Start/restart game
  const startGame = () => {
    // SECURITY: Only logged-in users can contribute to burn meter and leaderboard
    if (!user) {
      alert('🔒 Please login to play and save your scores to the leaderboard!')
      navigate('/login')
      return
    }
    
    // Check if logged in user can play
    if (user && burnMeterFull) {
      alert('🔒 Burn meter is full! Gameplay is locked until tokens are burned. Check the Burn Dashboard for details.')
      return
    }
    
    console.log('🎮 Starting game...')
    
    gameLoopRunning.current = false
    
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current)
      animationRef.current = null
    }
    
    setTimeout(() => {
      gameState.current = {
        bunnyY: CANVAS_HEIGHT / 2 - BUNNY_SIZE / 2,
        bunnyVelocity: 0,
        candles: [],
        moons: [],
        frameCount: 0,
        starOffset: 0,
        cloudOffset: 0,
        localScore: 0,
        localDifficulty: 1,
        graceFrames: 180
      }
      
      setScore(0)
      setDifficulty(1)
      setScoreSubmitted(false)
      setGameOver(false)
      setGameStarted(true)
      
      setTimeout(() => {
        console.log('🚀 Game loop starting')
        gameLoopRunning.current = true
        runGameLoop()
      }, 100)
    }, 100)
  }

  // Render just the background (for start screen)
  const renderBackground = () => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    // Clean space gradient background
    const gradient = ctx.createLinearGradient(0, 0, 0, CANVAS_HEIGHT)
    gradient.addColorStop(0, '#1a1a3e')
    gradient.addColorStop(0.5, '#2d1b4e')
    gradient.addColorStop(1, '#1a1a3e')
    ctx.fillStyle = gradient
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT)

    // Detailed twinkling stars
    for (let i = 0; i < 150; i++) {
      const starX = (i * 97) % CANVAS_WIDTH
      const starY = (i * 73) % (CANVAS_HEIGHT - 60)
      const size = 1 + (i % 4)
      
      ctx.globalAlpha = 0.3 + (i % 4) * 0.15
      ctx.fillStyle = i % 7 === 0 ? '#fef3c7' : '#ffffff'
      
      if (size > 2) {
        ctx.shadowBlur = 4
        ctx.shadowColor = ctx.fillStyle
      }
      
      ctx.beginPath()
      ctx.arc(starX, starY, size / 2, 0, Math.PI * 2)
      ctx.fill()
      ctx.shadowBlur = 0
    }
    ctx.globalAlpha = 1

    // Clean cloud shapes
    ctx.fillStyle = 'rgba(100, 100, 200, 0.3)'
    for (let i = 0; i < 4; i++) {
      const cloudX = (i * 250) % (CANVAS_WIDTH + 200) - 100
      const cloudY = 80 + i * 100
      
      ctx.beginPath()
      ctx.arc(cloudX, cloudY, 30, 0, Math.PI * 2)
      ctx.arc(cloudX + 25, cloudY, 35, 0, Math.PI * 2)
      ctx.arc(cloudX + 50, cloudY, 30, 0, Math.PI * 2)
      ctx.fill()
    }

    // Crescent moon
    const moonX = CANVAS_WIDTH - 100
    const moonY = 80
    const moonRadius = 35
    
    const moonGrad = ctx.createRadialGradient(moonX - 10, moonY - 10, 5, moonX, moonY, moonRadius)
    moonGrad.addColorStop(0, '#fff9e6')
    moonGrad.addColorStop(0.6, '#ffd700')
    moonGrad.addColorStop(1, '#ffa500')
    ctx.fillStyle = moonGrad
    ctx.beginPath()
    ctx.arc(moonX, moonY, moonRadius, 0, Math.PI * 2)
    ctx.fill()
    
    ctx.fillStyle = gradient
    ctx.beginPath()
    ctx.arc(moonX + 15, moonY - 5, moonRadius, 0, Math.PI * 2)
    ctx.fill()
    
    // Realistic planets
    const purpleX = CANVAS_WIDTH * 0.2
    const purpleY = CANVAS_HEIGHT * 0.25
    const purpleRadius = 20
    const purpleGrad = ctx.createRadialGradient(purpleX - 8, purpleY - 8, 2, purpleX, purpleY, purpleRadius)
    purpleGrad.addColorStop(0, '#e9d5ff')
    purpleGrad.addColorStop(0.3, '#c084fc')
    purpleGrad.addColorStop(0.7, '#9333ea')
    purpleGrad.addColorStop(1, '#581c87')
    ctx.fillStyle = purpleGrad
    ctx.beginPath()
    ctx.arc(purpleX, purpleY, purpleRadius, 0, Math.PI * 2)
    ctx.fill()
    ctx.fillStyle = 'rgba(147, 51, 234, 0.3)'
    ctx.fillRect(purpleX - purpleRadius, purpleY - 3, purpleRadius * 2, 6)

    const blueX = CANVAS_WIDTH * 0.85
    const blueY = CANVAS_HEIGHT * 0.6
    const blueRadius = 12
    const blueGrad = ctx.createRadialGradient(blueX - 4, blueY - 4, 2, blueX, blueY, blueRadius)
    blueGrad.addColorStop(0, '#dbeafe')
    blueGrad.addColorStop(0.3, '#60a5fa')
    blueGrad.addColorStop(0.7, '#1d4ed8')
    blueGrad.addColorStop(1, '#1e3a8a')
    ctx.fillStyle = blueGrad
    ctx.beginPath()
    ctx.arc(blueX, blueY, blueRadius, 0, Math.PI * 2)
    ctx.fill()
    ctx.fillStyle = 'rgba(255, 255, 255, 0.3)'
    ctx.beginPath()
    ctx.ellipse(blueX - 2, blueY, 5, 3, 0.3, 0, Math.PI * 2)
    ctx.fill()
    
    const tealX = CANVAS_WIDTH * 0.1
    const tealY = CANVAS_HEIGHT * 0.7
    const tealRadius = 10
    const tealGrad = ctx.createRadialGradient(tealX - 3, tealY - 3, 1, tealX, tealY, tealRadius)
    tealGrad.addColorStop(0, '#ccfbf1')
    tealGrad.addColorStop(0.3, '#5eead4')
    tealGrad.addColorStop(0.7, '#0d9488')
    tealGrad.addColorStop(1, '#134e4a')
    ctx.fillStyle = tealGrad
    ctx.beginPath()
    ctx.arc(tealX, tealY, tealRadius, 0, Math.PI * 2)
    ctx.fill()
    ctx.fillStyle = 'rgba(255, 255, 255, 0.6)'
    ctx.beginPath()
    ctx.arc(tealX, tealY - tealRadius + 3, 4, 0, Math.PI * 2)
    ctx.fill()

    // Ground
    const groundY = CANVAS_HEIGHT - 80
    const groundGrad = ctx.createLinearGradient(0, groundY, 0, CANVAS_HEIGHT)
    groundGrad.addColorStop(0, '#4b5563')
    groundGrad.addColorStop(0.5, '#374151')
    groundGrad.addColorStop(1, '#1f2937')
    ctx.fillStyle = groundGrad
    ctx.fillRect(0, groundY, CANVAS_WIDTH, 80)
    
    ctx.fillStyle = '#6b7280'
    for (let i = 0; i < CANVAS_WIDTH / 40; i++) {
      const x = i * 40
      ctx.fillRect(x, groundY, 35, 5)
    }
  }

  // Game loop
  const runGameLoop = () => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const state = gameState.current

    const gameLoop = () => {
      if (!gameLoopRunning.current) return

      state.frameCount++
      
      if (state.graceFrames > 0) {
        state.graceFrames--
      }

      // Update physics after grace period
      if (state.graceFrames === 0) {
        state.bunnyVelocity += GRAVITY
        state.bunnyY += state.bunnyVelocity

        // Boundary check
        if (state.bunnyY <= 0 || state.bunnyY + BUNNY_SIZE >= CANVAS_HEIGHT - 80) {
          console.log('💥 Boundary hit!')
          endGame()
          return
        }
      }

      // Spawn candlestick obstacles with varying gaps and heights (desktop-optimized)
      const spawnInterval = isDesktop ? 100 : 90
      if (state.graceFrames === 0 && state.frameCount % spawnInterval === 0) {
        // Desktop gets wider gaps for better playability
        const baseGap = isDesktop ? 180 : 150
        const gapVariation = isDesktop ? 80 : 70
        const gap = baseGap + Math.random() * gapVariation
        const groundY = CANVAS_HEIGHT - 80
        const minHeight = isDesktop ? 80 : 60
        const maxHeight = groundY - gap - minHeight
        // More random variation in heights
        const topHeight = minHeight + Math.random() * (maxHeight - minHeight)
        const bottomY = topHeight + gap
        const bottomHeight = groundY - bottomY
        
        // Desktop gets slightly wider candles for visibility
        const candleWidth = isDesktop ? 55 : 50
        
        state.candles.push(
          { x: CANVAS_WIDTH, y: 0, width: candleWidth, height: topHeight, isTop: true },
          { x: CANVAS_WIDTH, y: bottomY, width: candleWidth, height: bottomHeight, isTop: false }
        )
        
        // Spawn moon between every 2nd or 3rd set of candles
        const moonSpawnInterval = isDesktop ? 200 : 180
        if (state.frameCount % moonSpawnInterval === 0 || Math.random() > 0.5) {
          const moonY = topHeight + gap / 2 - 20
          const moonSize = isDesktop ? 45 : 40
          state.moons.push({
            x: CANVAS_WIDTH + (isDesktop ? 180 : 150),
            y: moonY,
            size: moonSize,
            collected: false
          })
        }
      }

      // Move candles
      const speed = BASE_SPEED * state.localDifficulty
      state.candles.forEach(candle => candle.x -= speed)
      state.candles = state.candles.filter(c => c.x + c.width > 0)
      
      // Move moons
      state.moons.forEach(moon => moon.x -= speed)
      state.moons = state.moons.filter(m => m.x + m.size > 0)

      // Collision detection
      const bunnyHitbox = {
        x: BUNNY_X + 10,
        y: state.bunnyY + 10,
        width: BUNNY_SIZE - 20,
        height: BUNNY_SIZE - 20
      }

      // Check candle collisions
      for (const candle of state.candles) {
        if (
          bunnyHitbox.x < candle.x + candle.width &&
          bunnyHitbox.x + bunnyHitbox.width > candle.x &&
          bunnyHitbox.y < candle.y + candle.height &&
          bunnyHitbox.y + bunnyHitbox.height > candle.y
        ) {
          console.log('💥 Candle collision!')
          endGame()
          return
        }
      }
      
      // Check moon collection
      for (const moon of state.moons) {
        if (!moon.collected) {
          const moonCenterX = moon.x + moon.size / 2
          const moonCenterY = moon.y + moon.size / 2
          const bunnyCenterX = BUNNY_X + BUNNY_SIZE / 2
          const bunnyCenterY = state.bunnyY + BUNNY_SIZE / 2
          const distance = Math.sqrt(
            Math.pow(moonCenterX - bunnyCenterX, 2) + 
            Math.pow(moonCenterY - bunnyCenterY, 2)
          )
          
          if (distance < (moon.size / 2 + BUNNY_SIZE / 2)) {
            moon.collected = true
            state.localScore++
            setScore(state.localScore)
            console.log('🌙 Moon collected! Total:', state.localScore)
            
            // Play collection sound
            if (moonSoundRef.current) {
              moonSoundRef.current.currentTime = 0
              moonSoundRef.current.play().catch(err => console.log('Sound play failed:', err))
            }
          }
        }
      }

      // Difficulty increase
      if (state.frameCount % 600 === 0) {
        state.localDifficulty = Math.min(state.localDifficulty + 0.2, 3)
        setDifficulty(state.localDifficulty)
      }

      // Parallax scrolling
      state.starOffset += speed * 0.5
      state.cloudOffset += speed * 0.3

      // Render collected moons (remove them from array)
      state.moons = state.moons.filter(m => !m.collected)

      // ===== RENDER FLAPPY BIRD STYLE =====
      
      // Render background first
      renderBackground()
      
      // Then render dynamic elements on top
      const renderCtx = canvas.getContext('2d')
      if (!renderCtx) return
      
      // Update star offset for parallax
      const gradient = renderCtx.createLinearGradient(0, 0, 0, CANVAS_HEIGHT)
      gradient.addColorStop(0, '#1a1a3e')
      gradient.addColorStop(0.5, '#2d1b4e')
      gradient.addColorStop(1, '#1a1a3e')
      renderCtx.fillStyle = gradient
      renderCtx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT)

      // Detailed twinkling stars
      for (let i = 0; i < 150; i++) {
        const baseX = (i * 97 % CANVAS_WIDTH)
        const starX = (baseX - state.starOffset * 0.3) % CANVAS_WIDTH
        if (starX < 0) continue
        const starY = (i * 73) % (CANVAS_HEIGHT - 60)
        const size = 1 + (i % 4)
        const twinkle = Math.sin(state.frameCount * 0.05 + i) * 0.3 + 0.7
        
        renderCtx.globalAlpha = twinkle * (0.3 + (i % 4) * 0.15)
        renderCtx.fillStyle = i % 7 === 0 ? '#fef3c7' : '#ffffff'
        
        // Star glow
        if (size > 2) {
          renderCtx.shadowBlur = 4
          renderCtx.shadowColor = renderCtx.fillStyle
        }
        
        renderCtx.beginPath()
        renderCtx.arc(starX, starY, size / 2, 0, Math.PI * 2)
        renderCtx.fill()
        renderCtx.shadowBlur = 0
      }
      renderCtx.globalAlpha = 1

      // Clean cloud shapes (like Flappy Bird)
      renderCtx.fillStyle = 'rgba(100, 100, 200, 0.3)'
      for (let i = 0; i < 4; i++) {
        const cloudX = ((i * 250 - state.cloudOffset) % (CANVAS_WIDTH + 200)) - 100
        const cloudY = 80 + i * 100
        
        renderCtx.beginPath()
        renderCtx.arc(cloudX, cloudY, 30, 0, Math.PI * 2)
        renderCtx.arc(cloudX + 25, cloudY, 35, 0, Math.PI * 2)
        renderCtx.arc(cloudX + 50, cloudY, 30, 0, Math.PI * 2)
        renderCtx.fill()
      }

      // Crescent moon (no shadow)
      const moonX = CANVAS_WIDTH - 100
      const moonY = 80
      const moonRadius = 35
      
      // Moon with gradient
      const moonGrad = renderCtx.createRadialGradient(moonX - 10, moonY - 10, 5, moonX, moonY, moonRadius)
      moonGrad.addColorStop(0, '#fff9e6')
      moonGrad.addColorStop(0.6, '#ffd700')
      moonGrad.addColorStop(1, '#ffa500')
      renderCtx.fillStyle = moonGrad
      renderCtx.beginPath()
      renderCtx.arc(moonX, moonY, moonRadius, 0, Math.PI * 2)
      renderCtx.fill()
      
      // Dark side for crescent
      renderCtx.fillStyle = gradient
      renderCtx.beginPath()
      renderCtx.arc(moonX + 15, moonY - 5, moonRadius, 0, Math.PI * 2)
      renderCtx.fill()
      
      // Realistic planets with gradients and details
      
      // Purple gas giant planet
      const purpleX = CANVAS_WIDTH * 0.2
      const purpleY = CANVAS_HEIGHT * 0.25
      const purpleRadius = 20
      const purpleGrad = renderCtx.createRadialGradient(purpleX - 8, purpleY - 8, 2, purpleX, purpleY, purpleRadius)
      purpleGrad.addColorStop(0, '#e9d5ff')
      purpleGrad.addColorStop(0.3, '#c084fc')
      purpleGrad.addColorStop(0.7, '#9333ea')
      purpleGrad.addColorStop(1, '#581c87')
      renderCtx.fillStyle = purpleGrad
      renderCtx.beginPath()
      renderCtx.arc(purpleX, purpleY, purpleRadius, 0, Math.PI * 2)
      renderCtx.fill()
      // Add atmospheric band
      renderCtx.fillStyle = 'rgba(147, 51, 234, 0.3)'
      renderCtx.fillRect(purpleX - purpleRadius, purpleY - 3, purpleRadius * 2, 6)
      

      // Earth-like blue planet
      const blueX = CANVAS_WIDTH * 0.85
      const blueY = CANVAS_HEIGHT * 0.6
      const blueRadius = 12
      const blueGrad = renderCtx.createRadialGradient(blueX - 4, blueY - 4, 2, blueX, blueY, blueRadius)
      blueGrad.addColorStop(0, '#dbeafe')
      blueGrad.addColorStop(0.3, '#60a5fa')
      blueGrad.addColorStop(0.7, '#1d4ed8')
      blueGrad.addColorStop(1, '#1e3a8a')
      renderCtx.fillStyle = blueGrad
      renderCtx.beginPath()
      renderCtx.arc(blueX, blueY, blueRadius, 0, Math.PI * 2)
      renderCtx.fill()
      // Add cloud patterns
      renderCtx.fillStyle = 'rgba(255, 255, 255, 0.3)'
      renderCtx.beginPath()
      renderCtx.ellipse(blueX - 2, blueY, 5, 3, 0.3, 0, Math.PI * 2)
      renderCtx.fill()
      
      // Teal ice planet
      const tealX = CANVAS_WIDTH * 0.1
      const tealY = CANVAS_HEIGHT * 0.7
      const tealRadius = 10
      const tealGrad = renderCtx.createRadialGradient(tealX - 3, tealY - 3, 1, tealX, tealY, tealRadius)
      tealGrad.addColorStop(0, '#ccfbf1')
      tealGrad.addColorStop(0.3, '#5eead4')
      tealGrad.addColorStop(0.7, '#0d9488')
      tealGrad.addColorStop(1, '#134e4a')
      renderCtx.fillStyle = tealGrad
      renderCtx.beginPath()
      renderCtx.arc(tealX, tealY, tealRadius, 0, Math.PI * 2)
      renderCtx.fill()
      // Add ice cap
      renderCtx.fillStyle = 'rgba(255, 255, 255, 0.6)'
      renderCtx.beginPath()
      renderCtx.arc(tealX, tealY - tealRadius + 3, 4, 0, Math.PI * 2)
      renderCtx.fill()

      // Re-render moving elements with parallax
      // (stars and clouds move with game)
      
      // CRYPTO CANDLESTICKS (replacing pipes)
      state.candles.forEach((candle) => {
        const isRed = candle.isTop
        
        // Main candle body with Flappy Bird style shading
        const bodyColor = isRed ? '#ef4444' : '#10b981'
        const darkColor = isRed ? '#b91c1c' : '#047857'
        const lightColor = isRed ? '#fca5a5' : '#6ee7b7'
        
        // Main body gradient
        const bodyGrad = renderCtx.createLinearGradient(candle.x, 0, candle.x + candle.width, 0)
        bodyGrad.addColorStop(0, lightColor)
        bodyGrad.addColorStop(0.5, bodyColor)
        bodyGrad.addColorStop(1, darkColor)
        
        renderCtx.fillStyle = bodyGrad
        renderCtx.fillRect(candle.x, candle.y, candle.width, candle.height)
        
        // Left highlight (Flappy Bird pipe style)
        renderCtx.fillStyle = lightColor
        renderCtx.fillRect(candle.x, candle.y, 8, candle.height)
        
        // Right shadow
        renderCtx.fillStyle = darkColor
        renderCtx.fillRect(candle.x + candle.width - 8, candle.y, 8, candle.height)
        
        // Candle cap (like pipe end)
        const capHeight = 15
        const capY = candle.isTop ? candle.y + candle.height - capHeight : candle.y
        const capGrad = renderCtx.createLinearGradient(candle.x, 0, candle.x + candle.width, 0)
        capGrad.addColorStop(0, lightColor)
        capGrad.addColorStop(0.5, bodyColor)
        capGrad.addColorStop(1, darkColor)
        
        renderCtx.fillStyle = capGrad
        renderCtx.fillRect(candle.x - 5, capY, candle.width + 10, capHeight)
        
        // Cap highlight
        renderCtx.fillStyle = lightColor
        renderCtx.fillRect(candle.x - 5, capY, 8, capHeight)
        
        // Cap shadow
        renderCtx.fillStyle = darkColor
        renderCtx.fillRect(candle.x + candle.width - 3, capY, 8, capHeight)
        
        // Wick (thin line in middle)
        renderCtx.fillStyle = 'rgba(0,0,0,0.3)'
        const wickX = candle.x + candle.width / 2 - 1
        renderCtx.fillRect(wickX, candle.y, 2, candle.height)
      })

      // Ground rendering
      const groundY = CANVAS_HEIGHT - 80
      const groundGrad = renderCtx.createLinearGradient(0, groundY, 0, CANVAS_HEIGHT)
      groundGrad.addColorStop(0, '#4b5563')
      groundGrad.addColorStop(0.5, '#374151')
      groundGrad.addColorStop(1, '#1f2937')
      renderCtx.fillStyle = groundGrad
      renderCtx.fillRect(0, groundY, CANVAS_WIDTH, 80)
      
      renderCtx.fillStyle = '#6b7280'
      for (let i = 0; i < CANVAS_WIDTH / 40; i++) {
        const x = i * 40
        renderCtx.fillRect(x, groundY, 35, 5)
      }
      
      // Render collectible moons
      state.moons.forEach(moon => {
        if (!moon.collected) {
          // Glowing crescent moon
          const moonRadius = moon.size / 2
          const moonCenterX = moon.x + moonRadius
          const moonCenterY = moon.y + moonRadius
          
          // Outer glow
          renderCtx.shadowBlur = 20
          renderCtx.shadowColor = '#ffd700'
          
          // Moon gradient
          const moonGrad = renderCtx.createRadialGradient(
            moonCenterX - 5, 
            moonCenterY - 5, 
            3, 
            moonCenterX, 
            moonCenterY, 
            moonRadius
          )
          moonGrad.addColorStop(0, '#fff9e6')
          moonGrad.addColorStop(0.6, '#ffd700')
          moonGrad.addColorStop(1, '#ffa500')
          renderCtx.fillStyle = moonGrad
          
          // Draw full moon
          renderCtx.beginPath()
          renderCtx.arc(moonCenterX, moonCenterY, moonRadius, 0, Math.PI * 2)
          renderCtx.fill()
          
          // Reset shadow for dark cutout
          renderCtx.shadowBlur = 0
          
          // Dark crescent cutout
          renderCtx.fillStyle = gradient
          renderCtx.beginPath()
          renderCtx.arc(moonCenterX + moonRadius * 0.4, moonCenterY - moonRadius * 0.15, moonRadius * 0.9, 0, Math.PI * 2)
          renderCtx.fill()
        }
      })

      // Bunny with clean style
      renderCtx.save()
      
      if (bunnyImageRef.current) {
        // Subtle glow
        renderCtx.shadowBlur = 15
        renderCtx.shadowColor = '#60a5fa'
        
        const rotation = Math.max(-0.3, Math.min(0.3, state.bunnyVelocity * 0.03))
        renderCtx.translate(BUNNY_X + BUNNY_SIZE / 2, state.bunnyY + BUNNY_SIZE / 2)
        renderCtx.rotate(rotation)
        
        renderCtx.drawImage(
          bunnyImageRef.current,
          -BUNNY_SIZE / 2,
          -BUNNY_SIZE / 2,
          BUNNY_SIZE,
          BUNNY_SIZE
        )
      } else {
        // Fallback
        renderCtx.fillStyle = '#ffffff'
        renderCtx.shadowBlur = 15
        renderCtx.shadowColor = '#60a5fa'
        renderCtx.beginPath()
        renderCtx.arc(BUNNY_X + BUNNY_SIZE / 2, state.bunnyY + BUNNY_SIZE / 2, BUNNY_SIZE / 2, 0, Math.PI * 2)
        renderCtx.fill()
      }
      
      renderCtx.restore()

      animationRef.current = requestAnimationFrame(gameLoop)
    }

    animationRef.current = requestAnimationFrame(gameLoop)
  }

  // Jump function
  const jump = () => {
    if (gameOver) {
      startGame()
      return
    }
    if (!gameStarted) {
      startGame()
      return
    }
    if (gameLoopRunning.current) {
      gameState.current.bunnyVelocity = JUMP_FORCE
    }
  }

  // Keyboard controls
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.code === 'Space' || e.code === 'ArrowUp') {
        e.preventDefault()
        jump()
      }
    }
    window.addEventListener('keydown', handleKeyPress)
    return () => window.removeEventListener('keydown', handleKeyPress)
  })

  // Touch controls for mobile
  useEffect(() => {
    const handleTouch = (e: TouchEvent) => {
      e.preventDefault()
      jump()
    }
    const canvas = canvasRef.current
    if (canvas) {
      canvas.addEventListener('touchstart', handleTouch, { passive: false })
      return () => canvas.removeEventListener('touchstart', handleTouch)
    }
  }, [gameOver, gameStarted])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      gameLoopRunning.current = false
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [])

  const handleSignOut = () => {
    signOut()
    navigate('/login')
  }

  return (
    <div className="fixed inset-0 bg-black flex flex-col items-center justify-center overflow-hidden touch-none">

      {/* Header - logged in users */}
      {user && (
        <div className="absolute top-2 md:top-4 right-2 md:right-4 z-20 flex gap-1 md:gap-2">
          <button
            onClick={() => navigate('/leaderboard')}
            className="px-2 md:px-3 py-1.5 md:py-2 bg-yellow-500 hover:bg-yellow-600 active:bg-yellow-700 text-black text-xs md:text-sm font-bold rounded-md transition-all duration-200 flex items-center gap-1"
          >
            <Award size={14} className="md:w-4 md:h-4" />
            <span className="hidden sm:inline">Leaderboard</span>
          </button>
          <button
            onClick={() => navigate('/profile')}
            className="px-2 md:px-3 py-1.5 md:py-2 bg-white hover:bg-gray-100 active:bg-gray-200 text-black text-xs md:text-sm font-bold rounded-md transition-all duration-200 flex items-center gap-1"
          >
            <User size={14} className="md:w-4 md:h-4" />
            <span className="hidden sm:inline">{user?.userName || 'Profile'}</span>
          </button>
          <button
            onClick={handleSignOut}
            className="px-2 md:px-3 py-1.5 md:py-2 bg-red-600 hover:bg-red-700 active:bg-red-800 text-white text-xs md:text-sm font-bold rounded-md transition-all duration-200 flex items-center gap-1"
          >
            <LogOut size={14} className="md:w-4 md:h-4" />
            <span className="hidden sm:inline">Exit</span>
          </button>
        </div>
      )}

      {/* Menu button - always visible */}
      <div className="absolute top-2 md:top-4 left-2 md:left-4 z-20">
        <button
          onClick={() => navigate('/')}
          className="px-3 md:px-4 py-1.5 md:py-2 bg-white hover:bg-gray-100 active:bg-gray-200 text-black text-xs md:text-sm font-bold rounded-md transition-all duration-200"
        >
          Menu
        </button>
      </div>

      {/* Logo */}
      <div className="absolute top-12 sm:top-16 md:top-20 left-1/2 -translate-x-1/2 text-center z-10 px-4">
        <img 
          src="https://static.lumi.new/5f/5ffb7827bd4e3986904b9e50ce1b0447.webp"
          alt="Rocket Bunny Logo"
          className="w-48 sm:w-56 md:w-80 drop-shadow-[0_0_20px_rgba(220,38,38,0.8)]"
        />
      </div>

      {/* Score Display */}
      <div className="absolute top-28 sm:top-36 md:top-40 left-1/2 -translate-x-1/2 flex gap-2 md:gap-3 z-10 px-2">
        <div className="bg-white px-2 sm:px-3 md:px-5 py-1 sm:py-1.5 md:py-2 rounded-lg shadow-xl">
          <p className="text-[8px] sm:text-[10px] md:text-xs text-gray-600 font-bold">MOONS 🌙</p>
          <p className="text-lg sm:text-xl md:text-3xl font-black text-yellow-500">{score}</p>
        </div>
        <div className="bg-yellow-400 px-2 sm:px-3 md:px-5 py-1 sm:py-1.5 md:py-2 rounded-lg shadow-xl">
          <p className="text-[8px] sm:text-[10px] md:text-xs text-black font-bold flex items-center gap-1">
            <Trophy size={10} className="md:w-3 md:h-3" /> BEST
          </p>
          <p className="text-lg sm:text-xl md:text-3xl font-black text-black">{highScore}</p>
        </div>
        <div className="bg-red-600 px-2 sm:px-3 md:px-5 py-1 sm:py-1.5 md:py-2 rounded-lg shadow-xl">
          <p className="text-[8px] sm:text-[10px] md:text-xs text-white font-bold">SPEED</p>
          <p className="text-lg sm:text-xl md:text-3xl font-black text-yellow-300">{difficulty.toFixed(1)}x</p>
        </div>
      </div>

      {/* Game Canvas - Full Screen */}
      <div className="fixed inset-0 z-0">
        <canvas
          ref={canvasRef}
          width={CANVAS_WIDTH}
          height={CANVAS_HEIGHT}
          onClick={jump}
          className="w-full h-full cursor-pointer touch-none"
        />

        {/* Start Screen */}
        {!gameStarted && !gameOver && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/40 px-4 z-20">
            <img 
              src="https://static.lumi.new/7b/7b3b9738bd14cb21ec880e638b7612fd.webp"
              alt="Rocket Bunny"
              className="w-16 h-16 sm:w-20 sm:h-20 md:w-32 md:h-32 mb-3 sm:mb-4 md:mb-6 drop-shadow-[0_0_40px_rgba(255,255,255,0.8)] animate-bounce"
            />
            <h2 className="text-2xl sm:text-3xl md:text-5xl font-black text-white mb-2 md:mb-4" style={{ textShadow: '0 0 20px rgba(220,38,38,0.8)' }}>
              GET READY!
            </h2>
            {!user ? (
              <>
                <p className="text-base sm:text-lg md:text-2xl text-yellow-400 mb-2 md:mb-4 font-bold text-center">🔒 Login Required to Play</p>
                <p className="text-xs sm:text-sm md:text-lg text-white mb-3 sm:mb-4 md:mb-6 font-bold text-center">Only logged-in users can save scores and contribute to burn meter</p>
                <button
                  onClick={() => navigate('/login')}
                  className="px-5 sm:px-6 md:px-10 py-2.5 sm:py-3 md:py-4 bg-yellow-500 hover:bg-yellow-600 active:bg-yellow-700 text-black text-base sm:text-lg md:text-xl font-black rounded-xl shadow-2xl active:scale-95 transition-all duration-200 touch-manipulation"
                >
                  🔑 LOGIN TO PLAY
                </button>
              </>
            ) : user && burnMeterFull ? (
              <>
                <p className="text-base sm:text-lg md:text-2xl text-red-500 mb-2 md:mb-4 font-black text-center animate-pulse">🔒 METER FULL - GAMEPLAY LOCKED</p>
                <p className="text-xs sm:text-sm md:text-lg text-yellow-400 mb-3 sm:mb-4 md:mb-6 font-bold text-center">Check Burn Dashboard for details</p>
                <button
                  onClick={() => navigate('/burn-dashboard')}
                  className="px-5 sm:px-6 md:px-10 py-2.5 sm:py-3 md:py-4 bg-yellow-500 hover:bg-yellow-600 active:bg-yellow-700 text-black text-base sm:text-lg md:text-xl font-black rounded-xl shadow-2xl active:scale-95 transition-all duration-200 touch-manipulation"
                >
                  🔥 VIEW BURN DASHBOARD
                </button>
              </>
            ) : (
              <>
                <p className="text-base sm:text-lg md:text-2xl text-yellow-400 mb-2 md:mb-4 font-bold text-center">Tap or SPACE to FLY!</p>
                <p className="text-xs sm:text-sm md:text-lg text-yellow-400 mb-3 sm:mb-4 md:mb-6 font-bold animate-pulse text-center">🌙 Collect Moons & Dodge Candles! 📉</p>
                <button
                  onClick={startGame}
                  className="px-5 sm:px-6 md:px-10 py-2.5 sm:py-3 md:py-4 bg-red-600 hover:bg-red-700 active:bg-red-800 text-white text-base sm:text-lg md:text-xl font-black rounded-xl shadow-2xl active:scale-95 transition-all duration-200 touch-manipulation"
                >
                  🚀 START GAME
                </button>
              </>
            )}
          </div>
        )}

        {/* Game Over Screen */}
        {gameOver && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/40 px-4 z-20">
            <h2 className="text-2xl sm:text-3xl md:text-5xl font-black text-red-600 mb-3 sm:mb-4 md:mb-6" style={{ textShadow: '0 0 20px rgba(220,38,38,0.8)' }}>
              GAME OVER
            </h2>
            <div className="bg-white px-5 sm:px-6 md:px-10 py-5 sm:py-6 md:py-8 rounded-2xl shadow-2xl mb-3 sm:mb-4 md:mb-6">
              <p className="text-xs sm:text-sm md:text-lg text-gray-600 mb-1 md:mb-2 font-bold">MOONS COLLECTED 🌙</p>
              <p className="text-4xl sm:text-5xl md:text-7xl font-black text-yellow-500">{score}</p>
              {score === highScore && score > 0 && (
                <p className="text-base sm:text-lg md:text-2xl text-yellow-500 mt-2 sm:mt-3 md:mt-4 animate-pulse font-black">
                  🎉 NEW RECORD! 🎉
                </p>
              )}
              {user && scoreSubmitted && (
                <p className="text-[10px] sm:text-xs md:text-sm text-green-600 mt-1.5 sm:mt-2 md:mt-3 font-bold">✅ Saved to leaderboard!</p>
              )}
              {!user && score > 0 && (
                <p className="text-[10px] sm:text-xs md:text-sm text-yellow-600 mt-1.5 sm:mt-2 md:mt-3 font-bold">🔒 Login to save your score!</p>
              )}
            </div>
            <div className="flex gap-2 sm:gap-3 md:gap-4">
              <button
                onClick={startGame}
                className="px-4 sm:px-6 md:px-10 py-2.5 sm:py-3 md:py-4 bg-yellow-400 hover:bg-yellow-500 active:bg-yellow-600 text-black text-base sm:text-lg md:text-xl font-black rounded-xl shadow-2xl active:scale-95 transition-all duration-200 touch-manipulation"
              >
                🔄 RETRY
              </button>
              <button
                onClick={() => navigate('/leaderboard')}
                className="px-4 sm:px-6 md:px-10 py-2.5 sm:py-3 md:py-4 bg-red-600 hover:bg-red-700 active:bg-red-800 text-white text-base sm:text-lg md:text-xl font-black rounded-xl shadow-2xl active:scale-95 transition-all duration-200 touch-manipulation"
              >
                🏆 BOARD
              </button>
            </div>
          </div>
        )}

        {/* Live controls hint */}
        {gameStarted && !gameOver && (
          <div className="absolute bottom-16 sm:bottom-20 md:bottom-24 left-1/2 -translate-x-1/2 z-10 px-2">
            <p className="text-yellow-400 text-xs sm:text-sm md:text-lg font-black drop-shadow-[0_0_10px_rgba(255,255,0,1)] animate-pulse text-center">
              👆 TAP or SPACE to FLY! 🚀
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

export default Game
